import React, { useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import AppBar from '@material-ui/core/AppBar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Button from '@material-ui/core/Button';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { useNotify } from 'react-admin';

import { useBoardsStyles } from './BoardsStyles';
import { useResponsive } from '../hooks/useResponsive';
import { MasterSheetContainer } from '../containers';
import { BoardTable } from './BoardTable';
import { MobileBoardTable } from './MobileBoardTable';
// Define interface locally instead of importing from mocks
interface BoardPurchase {
  id: string;
  isPrimary: boolean;
  canceled: boolean;
  poNumber: string;
  mudCount: number;
  boardPurchaseFloors: any[];
}

interface BoardsProps {
  geoLocationError?: string;
}

export const Boards: React.FC<BoardsProps> = ({ geoLocationError }) => {
  const classes = useBoardsStyles();
  const { isMobile } = useResponsive();
  const notify = useNotify();
  const { selectedJob, signJobReport, loadBoardsData } =
    MasterSheetContainer.useContainer();
  const [tabSelectedPO, setTabSelectedPO] = useState<number>(0);
  const [accordionsExpanded, setAccordionsExpanded] = useState<
    Record<string, boolean>
  >({});
  const [boardsData, setBoardsData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Load boards data when component mounts
  useEffect(() => {
    const loadData = async () => {
      if (!selectedJob?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await loadBoardsData(selectedJob.id);
        setBoardsData(data);
      } catch (error) {
        console.error('Error loading boards data:', error);
        notify(`Error loading boards: ${(error as any).message}`, 'error');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedJob?.id, notify]); // Removed loadBoardsData from dependencies

  // Initialize accordions state based on board purchases
  useEffect(() => {
    if (boardsData?.boardPurchases) {
      const initialAccordions = boardsData.boardPurchases.reduce(
        (acc: any, boardPurchase: any) => {
          acc[boardPurchase.id] = false;
          return acc;
        },
        {},
      );
      setAccordionsExpanded(initialAccordions);
    }
  }, [boardsData]);

  // Set primary PO as default selected tab
  useEffect(() => {
    if (boardsData?.boardPurchases) {
      const primaryIndex = boardsData.boardPurchases.findIndex(
        (bp: BoardPurchase) => bp.isPrimary && !bp.canceled,
      );
      setTabSelectedPO(primaryIndex >= 0 ? primaryIndex : 0);
    }
  }, [boardsData]);

  const onSignReport = async (floors: any, boardPurchase: BoardPurchase) => {
    if (!selectedJob?.id) {
      notify('No job selected', 'error');
      return;
    }

    try {
      const updatedBoardsData = await signJobReport(selectedJob.id, floors, boardPurchase);
      if (updatedBoardsData) {
        setBoardsData(updatedBoardsData);
      }
    } catch (error) {
      console.error('Error signing report:', error);
    }
  };

  const onCollapseAllClick = () => {
    setAccordionsExpanded(accordions => {
      const newValues = {};
      for (const key in accordions) {
        newValues[key] = false;
      }
      return newValues;
    });
  };

  const onExpandToggle = (id: string, value: boolean) => {
    setAccordionsExpanded(accordions => ({ ...accordions, [id]: value }));
  };

  const hasExpandedAccordions = Object.values(accordionsExpanded).some(
    value => value,
  );

  if (!selectedJob || loading) {
    return (
      <Box p={2}>
        <Typography>Loading boards data...</Typography>
      </Box>
    );
  }

  if (!boardsData?.boardPurchases || boardsData.boardPurchases.length === 0) {
    return (
      <Box p={2}>
        <Typography>No board purchases found for this job.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box className={classes.titleTab} px={2}>
        <Typography className={classes.titleFont}>Total Boards</Typography>
      </Box>

      <Box display="flex" alignItems="center" flexDirection="column" mt={1}>
        {geoLocationError && (
          <Typography className={classes.geoLocationError}>
            {geoLocationError}
          </Typography>
        )}

        {isMobile ? (
          <Box display="flex" flexDirection="column" width="100%" my={2}>
            {boardsData?.boardPurchases?.map((boardPurchase, index) => {
              if (boardPurchase.canceled) return null;

              return (
                <Box
                  key={boardPurchase.id}
                  className={`${classes.borderBox} ${classes.mobilePOWrapper}`}
                >
                  <MobileBoardTable
                    title={`PO #${index + 1}${
                      boardPurchase.orderLabel
                        ? ` - ${boardPurchase.orderLabel}`
                        : ''
                    }`}
                    expanded={accordionsExpanded[boardPurchase.id]}
                    onExpandToggle={onExpandToggle}
                    signReport={onSignReport}
                    boardPurchase={boardPurchase}
                    boardReceiptFloors={boardsData.boardReceiptFloors || []}
                    theJob={{
                      ...selectedJob,
                      boardTypes: boardsData.boardTypes,
                    }}
                  />
                </Box>
              );
            })}
            {hasExpandedAccordions && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                p={2}
              >
                <Button
                  variant="text"
                  endIcon={<ExpandLessIcon />}
                  onClick={onCollapseAllClick}
                >
                  Collapse all
                </Button>
              </Box>
            )}
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" width="100%">
            <AppBar position="static">
              <Tabs
                value={tabSelectedPO}
                onChange={(_event, newTab) => setTabSelectedPO(newTab)}
                aria-label="Vatos Tabs on House Report"
                centered
                indicatorColor="primary"
                variant="fullWidth"
                classes={{
                  root: classes.tabsBoards,
                  indicator: classes.indicatorBoards,
                }}
                textColor="secondary"
              >
                {boardsData?.boardPurchases.map((boardPurchase, index) => {
                  if (boardPurchase.canceled) return null;
                  return (
                    <Tab
                      value={index}
                      label={`PO ${index + 1}${
                        boardPurchase.orderLabel
                          ? ` - ${boardPurchase.orderLabel}`
                          : ''
                      } Total Boards`}
                      key={boardPurchase.id}
                    />
                  );
                })}
              </Tabs>
            </AppBar>
            <Box display="flex" flexDirection="column" width="100%" mt={2}>
              {boardsData?.boardPurchases.map((boardPurchase, index) => {
                if (boardPurchase.canceled) return null;

                return (
                  <Box
                    key={boardPurchase.id}
                    flexDirection="column"
                    display="flex"
                    width="100%"
                    className={tabSelectedPO === index ? '' : classes.none}
                  >
                    <BoardTable
                      signReport={onSignReport}
                      boardPurchase={boardPurchase}
                      boardReceiptFloors={boardsData.boardReceiptFloors || []}
                      theJob={{
                        ...selectedJob,
                        boardTypes: boardsData.boardTypes,
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Boards;
